/* The real system, right after the hero.
   Inline: a calm, non-interactive preview of the sign-in page (same origin, so the iframe is the app itself).
   Hover (or tap) anywhere on it → one «Open» button in the middle → the system opens in a full-screen modal
   where it is fully interactive, starting on sign-in with David's account pre-filled.
   The modal is only the system. A floating glass nav (start over · new window · close) slides in when the
   pointer moves to the top edge, and hides again by itself. Esc closes – also when the focus is inside the app. */
import { useCallback, useEffect, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ArrowUpRight, Maximize2, RotateCcw, X } from "lucide-react";
import { Dialog, DialogPortal } from "@/components/ui/dialog";
import { TOKEN_KEY } from "@/lib/api";

const clearSession = () => {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
};

/* how far from the top edge (px) the pointer has to be to reveal the nav */
const EDGE = 72;

export const LiveModal = ({ t, open, onOpenChange }) => {
  const [nonce, setNonce] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [navOn, setNavOn] = useState(true);
  const timer = useRef(null);
  const pinned = useRef(false);

  const hideLater = useCallback((ms) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (!pinned.current) setNavOn(false);
    }, ms);
  }, []);

  const reveal = useCallback(
    (ms = 1500) => {
      setNavOn(true);
      hideLater(ms);
    },
    [hideLater]
  );

  /* show the nav briefly when the system opens, so it is discovered – then get out of the way */
  useEffect(() => {
    if (open) {
      pinned.current = false;
      reveal(2800);
    }
    return () => clearTimeout(timer.current);
  }, [open, reveal]);

  const pin = () => {
    pinned.current = true;
    clearTimeout(timer.current);
    setNavOn(true);
  };
  const unpin = () => {
    pinned.current = false;
    hideLater(700);
  };

  const restart = () => {
    clearSession();
    setLoaded(false);
    setNonce((n) => n + 1);
  };

  /* same origin: listen inside the app for Esc (close) and for the pointer near the top edge (reveal nav) */
  const onFrameLoad = (e) => {
    setLoaded(true);
    try {
      const win = e.target.contentWindow;
      win.addEventListener("keydown", (ev) => {
        if (ev.key === "Escape") onOpenChange(false);
      });
      win.addEventListener(
        "mousemove",
        (ev) => {
          if (ev.clientY < EDGE) reveal(1500);
        },
        { passive: true }
      );
    } catch {
      /* ignore */
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogPrimitive.Overlay className="kvl-live-overlay" />
        <DialogPrimitive.Content className="kvp kvl kvl-tone-dark kvl-live-modal" aria-describedby={undefined} onOpenAutoFocus={(e) => e.preventDefault()} data-testid="tilbod-live-modal">
          <DialogPrimitive.Title className="sr-only">{t.eyebrow}</DialogPrimitive.Title>

          <div className="kvl-live-stage">
            {!loaded && (
              <div className="kvl-live-loading kvl-mono" data-testid="tilbod-live-modal-loading">
                {t.loading}
              </div>
            )}
            <iframe key={nonce} src={`/login?embed=1&n=${nonce}`} title="Skarpenord Configurator" className="kvl-live-frame" onLoad={onFrameLoad} allow="clipboard-write" data-testid="tilbod-live-modal-frame" />
          </div>

          {/* top edge: a thin hot strip (for pointers coming from outside the app) and a quiet handle */}
          <div className="kvl-live-hot" onMouseEnter={() => reveal(1500)} aria-hidden="true" />
          <button type="button" className={`kvl-live-handle ${navOn ? "" : "is-on"}`} onClick={() => reveal(3200)} aria-label={t.showControls} data-testid="tilbod-live-handle" />

          <div className={`kvl-live-nav ${navOn ? "is-on" : ""}`} onMouseEnter={pin} onMouseLeave={unpin} onFocusCapture={pin} onBlurCapture={unpin} role="toolbar" aria-label={t.eyebrow} data-testid="tilbod-live-nav">
            <button type="button" className="kvl-live-nav-btn" onClick={restart} aria-label={t.restart} data-testid="tilbod-live-restart">
              <RotateCcw size={15} aria-hidden="true" />
              <span className="kvl-live-nav-lbl">{t.restart}</span>
            </button>
            <a className="kvl-live-nav-btn" href="/login" target="_blank" rel="noreferrer" aria-label={t.open} data-testid="tilbod-live-newtab">
              <ArrowUpRight size={15} aria-hidden="true" />
              <span className="kvl-live-nav-lbl">{t.open}</span>
            </a>
            <span className="kvl-live-nav-sep" aria-hidden="true" />
            <DialogPrimitive.Close asChild>
              <button type="button" className="kvl-live-nav-btn kvl-live-nav-close" data-testid="tilbod-live-close">
                <X size={15} aria-hidden="true" />
                {t.close}
                <kbd className="kvl-live-kbd">Esc</kbd>
              </button>
            </DialogPrimitive.Close>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};

export const LiveSystem = ({ d, open: openProp, onOpenChange, compact = false }) => {
  const ref = useRef(null);
  const [near, setNear] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [openState, setOpenState] = useState(false);
  const controlled = typeof openProp === "boolean";
  const open = controlled ? openProp : openState;
  const setOpen = controlled ? onOpenChange : setOpenState;
  const t = d.live;

  /* mount the preview only when the section is about to be seen */
  useEffect(() => {
    const el = ref.current;
    if (!el || compact) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setNear(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true);
          io.disconnect();
        }
      },
      { rootMargin: "900px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* on a phone: no inline preview – one button that opens the system full screen (the app has its own phone layout) */
  if (compact) {
    return (
      <section id="live" className="kvl-live kvl-live-compact kvl-tone-paper" ref={ref} data-head={t.eyebrow} data-tone="paper" data-testid="tilbod-live">
        <div className="kvl-col">
          <button type="button" className="kvl-live-cta kvl-live-cta-block" onClick={() => setOpen(true)} data-testid="tilbod-live-open">
            <Maximize2 size={18} aria-hidden="true" />
            {t.openFull}
          </button>
          <p className="kvl-live-compact-sub" data-testid="tilbod-live-compact-sub">
            {t.ctaSub} · {t.hint}
          </p>
        </div>
        <LiveModal t={t} open={open} onOpenChange={setOpen} />
      </section>
    );
  }

  /* wide: no heading – just the prototype, with room above it */
  return (
    <section id="live" className="kvl-live kvl-live-bare kvl-tone-paper" ref={ref} data-head={t.eyebrow} data-tone="paper" data-testid="tilbod-live">
      <div className="kvl-live-wide">
        <div className={`kvl-live-card ${loaded ? "is-loaded" : ""}`} data-testid="tilbod-live-card">
          <div className="kvl-live-stage kvl-live-preview">
            {!loaded && (
              <div className="kvl-live-loading kvl-mono" data-testid="tilbod-live-loading">
                {t.loading}
              </div>
            )}
            {near && <iframe src="/login?embed=1&preview=1" title="" className="kvl-live-frame kvl-live-frame-preview" onLoad={() => setLoaded(true)} tabIndex={-1} aria-hidden="true" loading="lazy" data-testid="tilbod-live-frame" />}
            <button type="button" className="kvl-live-cover" onClick={() => setOpen(true)} data-testid="tilbod-live-open">
              <span className="kvl-live-dim" aria-hidden="true" />
              <span className="kvl-live-cta-wrap">
                <span className="kvl-live-cta">
                  <Maximize2 size={18} aria-hidden="true" />
                  {t.openFull}
                </span>
                <span className="kvl-live-cta-sub kvl-mono">{t.ctaSub}</span>
              </span>
            </button>
          </div>
        </div>
      </div>
      <LiveModal t={t} open={open} onOpenChange={setOpen} />
    </section>
  );
};
